import { DetailedInsight } from '@api/api/insights';
import { queryOptions, useQueries, useQuery } from '@tanstack/react-query';
import { getWretchInsight } from '@terminal/services/insights/insights-service';

export const QUERY_KEY_INSIGHT = 'QUERY_KEY_INSIGHT';

export const createInsightQueryOptions = (insightId: string) =>
  queryOptions({
    queryKey: [QUERY_KEY_INSIGHT, insightId],
    queryFn: () => getWretchInsight(insightId),
    enabled: !!insightId,
    staleTime: 1000 * 60 * 60 * 1, // 1 hour
  });

export const useInsight = (insightId: string) =>
  useQuery(createInsightQueryOptions(insightId));

export const useInsightsById = (insightIds: string[]) =>
  useQueries({
    queries: insightIds.map(createInsightQueryOptions),
    combine: results =>
      new Map(
        results.flatMap(result =>
          result.data ? [[result.data.id, result.data] as const] : []
        )
      ),
  });

export const useQueryInsights = (insightsId: string[], enabled = true) => {
  const queries = insightsId.map(insightId => ({
    ...createInsightQueryOptions(insightId),
    enabled,
  }));
  return useQueries({
    queries,
    combine: results => {
      const hasResult = !!results.length;
      return {
        data: results
          .map(result => result.data)
          .filter(Boolean) as DetailedInsight[],
        isLoading: hasResult && results.some(result => result.isLoading),
        isFetching:
          hasResult &&
          results.some(result => result.isFetching || result.isRefetching),
        isError: hasResult && results.every(result => result.isError),
        refetch: () => {
          results.forEach(result => result.refetch());
        },
      };
    },
  });
};
